import { Tag, Typography } from 'antd';
import {
  TeamOutlined,
  ProjectOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { format } from '@/theme/format';
import { semantic } from '@/theme/tokens';
import type { Contract, CurrencyCode } from '@/mocks/types';

const { Title, Text } = Typography;

export interface ContractHeroBannerProps {
  /** Contract code (CTR-26-001) */
  code: string;
  /** Contract type — drives accent + label */
  type: 'client' | 'subcontract';
  /** Counterparty name (client for client contracts, supplier for subs) */
  counterpartyName: string;
  /** Linked project name */
  projectName: string;
  /** Project code */
  projectCode: string;
  /** Original signed contract value */
  originalValue: number;
  /** Current value (original + approved VOs) */
  currentValue: number;
  /** Currency */
  currency: CurrencyCode;
  /** Lifecycle status */
  status: Contract['status'];
  /** Start date (ISO) */
  startDate?: string;
  /** End date (ISO) */
  endDate?: string;
  /** Number of approved VOs */
  voCount?: number;
  /** Number of issued IPCs */
  ipcCount?: number;
  /** Number of claims */
  claimsCount?: number;
}

/**
 * Hero banner for the contract detail drawer. Mirrors ProjectHeroBanner
 * with contracts-specific surfaces: type pill (client / subcontract),
 * linked project, and a small chip-row of activity counts (VOs / IPCs /
 * claims) — these are the most-asked questions about a contract from the
 * commercial team's perspective and surfacing them at the top saves a
 * tab-click on the demo path.
 */
export default function ContractHeroBanner({
  code,
  type,
  counterpartyName,
  projectName,
  projectCode,
  originalValue,
  currentValue,
  currency,
  status,
  startDate,
  endDate,
  voCount,
  ipcCount,
  claimsCount,
}: ContractHeroBannerProps) {
  const { t, i18n } = useTranslation(['contracts', 'common']);
  const isRtl = i18n.language === 'ar';

  // Brand-aligned status palette.
  const statusColor = (() => {
    switch (status) {
      case 'draft':
        return semantic.status.neutral;
      case 'active':
        return semantic.status.success;
      case 'suspended':
        return semantic.status.warning;
      case 'closed':
        return semantic.status.info;
      default:
        return semantic.brand.primary;
    }
  })();

  const valueDelta = currentValue - originalValue;

  return (
    <div
      style={{
        position: 'relative',
        margin: '-24px -24px 24px',
        padding: '24px 24px 20px',
        background: `linear-gradient(135deg, ${semantic.brand.primary} 0%, ${semantic.brand.primaryActive} 100%)`,
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Atmospheric mark — abstract document/seal motif. SVG, ~12% opacity. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 800 200"
        preserveAspectRatio="xMaxYMid slice"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.14,
          pointerEvents: 'none',
        }}
      >
        {/* Stamp/seal circle on the right */}
        <g transform="translate(700, 100)">
          <circle r="60" fill="none" stroke={semantic.brand.accent} strokeWidth="1.4" />
          <circle r="48" fill="none" stroke={semantic.brand.secondary} strokeWidth="1" />
          <circle r="2.5" fill={semantic.brand.accent} />
          <line x1="-24" y1="0" x2="24" y2="0" stroke={semantic.brand.accent} strokeWidth="1" />
          <line x1="0" y1="-24" x2="0" y2="24" stroke={semantic.brand.accent} strokeWidth="1" />
        </g>
        {/* Contract lines on the left, suggestive of paper */}
        <g transform="translate(40, 60)" stroke={semantic.brand.secondary} strokeWidth="1.2">
          <line x1="0" y1="0" x2="220" y2="0" />
          <line x1="0" y1="14" x2="200" y2="14" />
          <line x1="0" y1="28" x2="240" y2="28" />
          <line x1="0" y1="42" x2="180" y2="42" />
          <line x1="0" y1="56" x2="220" y2="56" />
          <line x1="0" y1="70" x2="160" y2="70" />
        </g>
      </svg>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <Text
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                display: 'block',
                marginBottom: 6,
              }}
            >
              {code} · {type === 'client' ? 'Client contract' : 'Subcontract'}
            </Text>
            <Title
              level={3}
              style={{ color: '#fff', margin: 0, fontSize: 22, lineHeight: 1.2 }}
            >
              {counterpartyName}
            </Title>
            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 10,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Tag
                color={statusColor}
                style={{
                  margin: 0,
                  fontWeight: 600,
                  border: `1px solid ${statusColor}`,
                  background: 'rgba(255, 255, 255, 0.16)',
                  color: '#fff',
                  textTransform: 'capitalize',
                }}
              >
                {status}
              </Tag>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: 'rgba(255, 255, 255, 0.78)',
                }}
              >
                <ProjectOutlined /> {projectCode} · {projectName}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: 'rgba(255, 255, 255, 0.78)',
                }}
              >
                <TeamOutlined /> {counterpartyName}
              </span>
              {(startDate || endDate) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: 'rgba(255, 255, 255, 0.78)',
                  }}
                >
                  <CalendarOutlined />
                  {startDate ? format.date(startDate, 'short') : '—'}
                  {' → '}
                  {endDate ? format.date(endDate, 'short') : '—'}
                </span>
              )}
            </div>
            {/* Activity count chips */}
            {(voCount !== undefined || ipcCount !== undefined || claimsCount !== undefined) && (
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  marginTop: 14,
                  fontSize: 11,
                  color: 'rgba(255, 255, 255, 0.68)',
                }}
              >
                {voCount !== undefined && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <EditOutlined />
                    <strong style={{ color: '#fff', fontSize: 13 }}>{voCount}</strong> VO
                    {voCount === 1 ? '' : 's'}
                  </span>
                )}
                {ipcCount !== undefined && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <FileDoneOutlined />
                    <strong style={{ color: '#fff', fontSize: 13 }}>{ipcCount}</strong> IPC
                    {ipcCount === 1 ? '' : 's'}
                  </span>
                )}
                {claimsCount !== undefined && claimsCount > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <strong
                      style={{
                        color: claimsCount > 0 ? semantic.brand.accent : '#fff',
                        fontSize: 13,
                      }}
                    >
                      {claimsCount}
                    </strong>{' '}
                    claim{claimsCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            )}
          </div>
          <div
            style={{
              textAlign: isRtl ? 'left' : 'right',
              minWidth: 180,
              flexShrink: 0,
            }}
          >
            <Text
              style={{
                fontSize: 11,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.65)',
                fontWeight: 500,
                display: 'block',
              }}
            >
              {t('contracts:pages.profitability') || 'Current value'}
            </Text>
            <div
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: '#fff',
                lineHeight: 1.2,
                marginTop: 2,
              }}
            >
              {format.currency(currentValue, currency, { fractionDigits: 0 })}
            </div>
            {valueDelta !== 0 && (
              <Text
                style={{
                  display: 'block',
                  fontSize: 11,
                  color: valueDelta > 0 ? semantic.brand.accentSoft : 'rgba(255, 255, 255, 0.6)',
                  marginTop: 2,
                }}
              >
                {valueDelta > 0 ? '+' : ''}
                {format.currency(valueDelta, currency, { fractionDigits: 0 })} vs original
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
